import { useMemo } from 'react';
import { useHistory, withRouter } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import { deleteChapter as deleteChapterApi, getChapters } from 'api/chapter';
import Axios from 'api/client';
import Content from 'assets/js/components/Content';
import * as ExportDataDialog from 'assets/js/components/ExportDataDialog';
import * as AddChapterDialog from 'assets/js/pages/chapters/AddChapterDialog';
import SplashScreen from 'assets/js/pages/chapters/Splash';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Chapter } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<Chapter>();

const Chapters = () => {
    const toast = useToast();
    const history = useHistory();
    const { accountId, access } = useCurrentAccount();
    const { pagination, setPagination } = Table.useReactTableState();
    const [search, setSearch] = usePersistentSearch();
    const searchTerm = useDebounce(search);

    const [isPending, params] = useIsPending(
        {
            query: searchTerm,
            page: pagination.pageIndex + 1,
            per_page: pagination.pageSize,
        },
        500,
    );
    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.chapters(accountId, params),
        queryFn: () => getChapters(accountId, params),
    });

    const isChaptersEnabled = !!access.pages.find(value => value === 'chapters');

    const onSubmitExport = () => {
        return new Promise((resolve, reject) => {
            let url = `/accounts/${accountId}/reports`;

            Axios.post(url, {
                scope: 'all',
                object: 'account',
                report_type: 'csv',
                resource: 'chapters',
                object_id: accountId.toString(),
            })
                .then(() => {
                    resolve(null);
                })
                .catch((e: GivebutterAxiosError) => {
                    reject(e);
                });
        });
    };

    const { mutate: deleteChapter } = useMutation({
        mutationFn: (chapterId: number) => deleteChapterApi(accountId, chapterId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.chapters(accountId, params),
            });
            toast.success(
                <>
                    <span className="font-medium">Success!</span> Deleted chapter successfully.
                </>,
            );
        },
        onError: () => {
            toast.error(
                <>
                    <span className="font-medium">Whoops.</span> Something went wrong. Please try
                    again.
                </>,
            );
        },
    });

    const columns = useMemo(() => {
        const viewChapter = (accountId: number) => history.push(`/accounts/${accountId}/home`);

        return [
            columnHelper.accessor('name', {
                header: 'Name',
                cell: info => {
                    const chapter = info.row.original;
                    return (
                        <Button
                            variant="transparent"
                            size="sm"
                            onClick={() => viewChapter(chapter.id)}
                        >
                            {chapter.display_name}
                        </Button>
                    );
                },
            }),
            columnHelper.accessor('members', {
                header: 'Member',
                cell: info => {
                    const chapter = info.row.original;
                    return (
                        <span className="whitespace-nowrap">
                            {`${chapter.members} member${chapter.members !== 1 ? 's' : ''}`}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('raised', {
                header: 'Raised',
                cell: info => {
                    const chapter = info.row.original;
                    return <>${maskDecimal(chapter.raised)}</>;
                },
            }),
            columnHelper.accessor('administrators', {
                header: 'Admins',
                cell: info => {
                    const chapter = info.row.original;
                    return (
                        <>
                            {chapter.administrators.map(admin => (
                                <a key={admin.email} href={`mailto:${admin.email}`}>
                                    {admin.first_name} {admin.last_name}
                                </a>
                            ))}
                        </>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const chapter = info.row.original;
                    return (
                        <DropdownMenu.Root key={chapter.id}>
                            <Table.RowDropdownTrigger />
                            <DropdownMenu.Content>
                                <DropdownMenu.Item asChild={true}></DropdownMenu.Item>
                                <AlertDialog.Root type={'error'}>
                                    <AlertDialog.Trigger asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Delete chapter
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>
                                                    Delete chapter {chapter.name}?
                                                </AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    You won't be able to undo this action. Are you
                                                    sure?
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() => {
                                                        deleteChapter(chapter.id);
                                                    }}
                                                >
                                                    Delete chapter
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ];
    }, [deleteChapter, history]);

    const tableState = {
        pagination,
    };

    const table = useReactTable({
        data: data?.data ?? [],
        columns: columns,
        pageCount: data?.meta.last_page ?? -1,
        initialState: tableState,
        state: tableState,
        manualPagination: true,
        enableSorting: false,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onPaginationChange: setPagination,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, data);

    return (
        <div className="flex flex-col gap-5">
            {isChaptersEnabled ? (
                <>
                    <div className={'flex justify-between gap-4'}>
                        <div className={'flex items-center gap-x-4'}>
                            <p className={'text-2xl font-medium leading-none'}>Chapters</p>
                            <div className={'h-6 w-px bg-gray-300'} />
                            <p className={'text-base font-medium leading-none'}>
                                {data?.meta.total ? maskWholeNumber(data.meta.total) : 0}
                            </p>
                        </div>
                        <div className={'flex flex-wrap items-center gap-4'}>
                            <Input.Root size={'sm'}>
                                <Input.SearchIcon />
                                <Input.Form
                                    value={search}
                                    placeholder={'Search'}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </Input.Root>
                            <div className={'flex flex-wrap items-center gap-4'}>
                                <ExportDataDialog.Root label="Chapters" onSubmit={onSubmitExport}>
                                    <ExportDataDialog.Trigger asChild={true}>
                                        <Button variant="outline" color="neutral">
                                            Export
                                        </Button>
                                    </ExportDataDialog.Trigger>
                                </ExportDataDialog.Root>
                                <AddChapterDialog.Root>
                                    <AddChapterDialog.Trigger asChild={true}>
                                        <Button variant="solid">New</Button>
                                    </AddChapterDialog.Trigger>
                                </AddChapterDialog.Root>
                            </div>
                        </div>
                    </div>
                    <Table.Root
                        isLoading={isPending}
                        table={table}
                        searchTerm={searchTerm}
                        variant="bordered"
                    >
                        <div className="flex flex-col gap-4">
                            <Box className="overflow-x-auto">
                                <Table.Content className={'w-full'}>
                                    <Table.Header />
                                    <Table.Body />
                                </Table.Content>
                                <Table.Empty variant={'primary'}>
                                    <Table.EmptyIcon />
                                    <Table.EmptyTitle>No chapters found</Table.EmptyTitle>
                                </Table.Empty>
                            </Box>
                            <Table.Pagination>
                                <Table.PaginationPageNavigation />
                                <Table.PaginationPerPageDropdown />
                            </Table.Pagination>
                        </div>
                    </Table.Root>
                </>
            ) : (
                <SplashScreen />
            )}
        </div>
    );
};

const ChaptersWrapper = () => {
    return (
        <Content>
            <Chapters />
        </Content>
    );
};

export default withRouter(ChaptersWrapper);
